import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar token ao inicializar
  useEffect(() => {
    checkAuth();
  }, []);

  // Verificar autenticação
  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Configurar token no header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Verificar token no backend
      const response = await api.get('/auth/verify');
      
      if (response.data.valid) {
        setUser(response.data.user);
        toast.success('Login restaurado com sucesso!');
      } else {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // Login
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      const { user, token } = response.data;
      
      // Salvar token
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Atualizar estado
      setUser(user);
      
      toast.success('Login realizado com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao fazer login';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Stores the JWT returned by a federated sign-in route.
   */
  const completeFederatedLogin = (response) => {
    const { user: signedInUser, token } = response.data;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(signedInUser);
    toast.success('Login realizado com sucesso!');
    return { success: true };
  };

  /**
   * Exchanges a Google ID token for the same JWT session used by password login.
   */
  const loginWithGoogle = async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      return completeFederatedLogin(response);
    } catch (error) {
      const message = error.response?.data?.error || 'Não foi possível entrar com Google.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Exchanges a Microsoft ID token for the same JWT session used by password login.
   */
  const loginWithMicrosoft = async (credential) => {
    try {
      const response = await api.post('/auth/microsoft', { credential });
      return completeFederatedLogin(response);
    } catch (error) {
      const message = error.response?.data?.error || 'Não foi possível entrar com Microsoft.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Registro
  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      const { user, token } = response.data;
      
      // Salvar token
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Atualizar estado
      setUser(user);
      
      toast.success('Conta criada com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao criar conta';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      // Chamar logout no backend
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      toast.success('Logout realizado com sucesso!');
    }
  };

  // Atualizar perfil
  /**
   * Saves profile fields except the login email, which stays the address used to sign in.
   */
  const updateProfile = async (profileData) => {
    try {
      const editable = { ...(profileData || {}) };
      delete editable.email;
      const response = await api.put('/users/profile', editable);

      setUser(prev => ({ ...prev, ...editable, email: prev?.email }));
      
      toast.success('Perfil atualizado com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao atualizar perfil';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Atualizar avatar
  const updateAvatar = async (avatarBase64) => {
    try {
      const response = await api.post('/users/avatar', { avatar_base64: avatarBase64 });
      
      // Atualizar usuário no estado
      setUser(prev => ({ ...prev, avatar_base64: avatarBase64 }));
      
      toast.success('Avatar atualizado com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao atualizar avatar';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Remover avatar
  const removeAvatar = async () => {
    try {
      await api.delete('/users/avatar');
      
      // Atualizar usuário no estado
      setUser(prev => ({ ...prev, avatar_base64: null }));
      
      toast.success('Avatar removido com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao remover avatar';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Alterar senha
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/users/change-password', {
        currentPassword,
        newPassword
      });
      
      toast.success('Senha alterada com sucesso!');
      
      // Fazer logout após alterar senha
      setTimeout(() => {
        logout();
      }, 2000);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao alterar senha';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    register,
    logout,
    updateProfile,
    updateAvatar,
    removeAvatar,
    changePassword,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
